/**
 * The ONE boot read of the stored credentials — the entry route, AuthContext
 * and the native Auth0 provider all decide "signed in or not" from it.
 *
 * WHY IT WAITS
 * ============
 * iOS starts Cedar with the phone locked (HealthKit delivery, a background
 * refresh), and a keychain item can refuse to be read until the phone is
 * unlocked. Each boot reader used to read the keychain itself, take the refusal
 * as "no token", and route to sign-in. kirsten.brouse 044901ed, 2026-10-14
 * 09:06 UTC: a valid session on the phone (the plain-storage copy authenticated
 * eight requests in the same second), sent to sign in anyway — and the profile
 * load that ran in that instant got no token, so the workout she then logged
 * was saved in kg.
 *
 * `unreadable` is therefore never an answer the boot acts on while the phone
 * might still unlock: the read waits for the app to come on screen (the phone
 * is unlocked then) or for a retry to succeed in the background. Only a
 * keychain that still refuses with the app ON SCREEN is given up on — that is
 * a keychain fault, not a locked phone, and it is reported as one.
 *
 * Each step reports to CloudWatch through the auth-free client-error route:
 * `NATIVE_CLIENT_ERROR surface=AuthBootKeychainUnreadable.<app state>`, then
 * `surface=AuthBootKeychainRecovered` or `surface=AuthBootKeychainGaveUp`.
 */
import { AppState } from 'react-native';
import { secureTokenStorage, type SecureRead } from '../services/secureTokenStorage';
import { reportClientError } from '../services/clientErrorReporter';

export interface BootCredentials {
  cedarJwt: SecureRead<string>;
  accessToken: SecureRead<string>;
  userData: SecureRead<any>;
}

/** On screen and still refused: tries before the boot gives up. */
const ON_SCREEN_TRIES = 3;
const ON_SCREEN_RETRY_MS = 1_000;
/** In the background: how often to try again while waiting to come on screen. */
const BACKGROUND_RETRY_MS = 10_000;

export function unreadableParts(creds: BootCredentials): string[] {
  return (Object.keys(creds) as Array<keyof BootCredentials>).filter(
    (k) => creds[k].state === 'unreadable',
  );
}

async function readOnce(): Promise<BootCredentials> {
  try {
    const [cedarJwt, accessToken, userData] = await Promise.all([
      secureTokenStorage.readCedarJwt(),
      secureTokenStorage.readAccessToken(),
      secureTokenStorage.readUserData(),
    ]);
    return { cedarJwt, accessToken, userData };
  } catch (error) {
    // The reads catch their own keychain errors; this is anything else. It is
    // not "no session" either.
    const e = error as { code?: unknown; name?: unknown } | null;
    const unreadable = { state: 'unreadable' as const, errorType: String(e?.code ?? e?.name ?? 'unknown') };
    return { cedarJwt: unreadable, accessToken: unreadable, userData: unreadable };
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Resolves when the app comes on screen, or after `ms` — whichever is first. */
function onScreenOrAfter(ms: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    let subscription: { remove: () => void } | null = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      subscription?.remove();
      resolve();
    };
    const timer = setTimeout(finish, ms);
    subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') finish();
    });
  });
}

function firstErrorType(creds: BootCredentials): string {
  for (const read of Object.values(creds)) {
    if (read.state === 'unreadable') return read.errorType;
  }
  return 'none';
}

async function readUntilReadable(): Promise<BootCredentials> {
  let creds = await readOnce();
  if (unreadableParts(creds).length === 0) return settle(creds);

  const startedAt = Date.now();
  const appState = String(AppState.currentState ?? 'unknown');
  console.error(
    `❌ AUTH_BOOT_KEYCHAIN_UNREADABLE parts=${unreadableParts(creds).join(',')} ` +
      `app_state=${appState} error=${firstErrorType(creds)} — waiting, not signing out`,
  );
  void reportClientError({ surface: `AuthBootKeychainUnreadable.${appState}` });

  let onScreenTries = 0;
  for (;;) {
    if (AppState.currentState === 'active') {
      if (onScreenTries >= ON_SCREEN_TRIES) break;
      onScreenTries += 1;
      await sleep(ON_SCREEN_RETRY_MS);
    } else {
      await onScreenOrAfter(BACKGROUND_RETRY_MS);
    }
    creds = await readOnce();
    if (unreadableParts(creds).length === 0) {
      console.log(`AUTH_BOOT_KEYCHAIN_RECOVERED waited_ms=${Date.now() - startedAt}`);
      void reportClientError({ surface: 'AuthBootKeychainRecovered' });
      return settle(creds);
    }
  }

  console.error(
    `❌ AUTH_BOOT_KEYCHAIN_GAVE_UP parts=${unreadableParts(creds).join(',')} ` +
      `error=${firstErrorType(creds)} — on screen and still refused; credentials kept`,
  );
  void reportClientError({ surface: 'AuthBootKeychainGaveUp' });
  return creds;
}

/** A fully readable keychain: move an older build's items AFTER_FIRST_UNLOCK. */
function settle(creds: BootCredentials): BootCredentials {
  void secureTokenStorage.migrateKeychainAccessibility();
  return creds;
}

let inFlight: Promise<BootCredentials> | null = null;

/**
 * The stored credentials, read once the keychain can answer. Readers that ask
 * while a read is in flight share it. Never rejects.
 */
export function readBootCredentials(): Promise<BootCredentials> {
  if (!inFlight) {
    inFlight = readUntilReadable().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

export function _resetBootCredentialsForTests(): void {
  inFlight = null;
}
